import { Store } from 'libx'
import { observable, action, runInAction, computed } from 'mobx'
import autobind from 'autobind-decorator'
import { browserHistory } from 'react-router'
import { task } from 'mobx-task'
// noinspection JSFileReferences
import ifvisible from 'ifvisible.js'
import links from 'misc/links'

@autobind
export default class ProjectDetailsStore extends Store {
  @observable project = null
  @observable composerText = ''
  @observable loadingMessages = true
  @observable notFound = true
  @observable detailsTab = 'INFO'

  constructor () {
    super(...arguments)
    this.rootStore.messageStore.onIncomingMessage(this.onIncomingMessage)
  }

  @computed get conversation () {
    if (!this.project) {
      return null
    }

    return this.rootStore.messageStore.conversations.get(this.project.conversationId)
  }

  @computed get messages () {
    if (!this.project) {
      return []
    }

    return this.rootStore.messageStore.messagesIn(this.project.conversationId)
  }

  @computed get unreadMessagesCount () {
    return this.messages.filter(x => x.isRead === false).length
  }

  @action changeComposerText (newValue) {
    this.composerText = newValue
  }

  @action activate (projectId) {
    const breakIfUnderConstruction = (p) => {
      if (p.status === 'UNDER_CONSTRUCTION') {
        browserHistory.replace(links.projectEditor(p.id))
        return true
      }

      return false
    }
    this.rootStore.rateProjectStore.ensureAllRated()
    this.project = null
    this.notFound = false
    this.composerText = ''
    this.loadingMessages = true
    this.detailsTab = 'INFO'

    const projectStore = this.rootStore.projectStore
    let project = projectStore.projects.get(projectId)
    if (project) {
      if (breakIfUnderConstruction(project)) {
        return
      }
      this.project = project
      return Promise.all([
        this.loadMessages(),
        this.loadDocuments(),
        this.loadEvents()
      ])
    }

    return projectStore.fetchProject(projectId)
      .then(action((project) => {
        if (project) {
          if (breakIfUnderConstruction(project)) {
            return
          }
        }
        this.project = project
        this.notFound = !project
        if (project) {
          this.loadMessages()
          this.loadDocuments()
          this.loadEvents()
        }
      }))
  }

  @action deactivate () {
    this.project = null
    this.notFound = false
    this.composerText = ''
  }

  @action changeDetailsTab (newTab) {
    this.detailsTab = newTab
  }

  @action stageFiles (files) {
    const category = this.project.documentsCategory
    const jobId = this.project.id
    return this.rootStore.documentStore.stageFiles(category, files, jobId)
  }

  /**
   * Triggered when a new message comes in from the wire.
   * We need to figure out what to do with it based on
   * what project the user is currently viewing.
   *
   * @param message
   */
  onIncomingMessage (message) {
    this.rootStore.soundStore.playGotMessage()
    if (this.project && this.project.conversationId === message.conversationId && ifvisible.now()) {
      // Don't notify with desktop notifications when we're already viewing the
      // messages for the conversation the message belongs to.
      return
    }

    this.rootStore.desktopNotificationStore.show({
      title: `New message from ${message.sender.name}`,
      icon: message.sender.avatar,
      body: message.text,
      onClick: async () => {
        const project = this.rootStore.projectStore.projectForConversation(message.conversationId)
        if (project) {
          browserHistory.push(links.projectDetails(project.id))
        }
      }
    })
  }

  async sendMessage () {
    if (!this.composerText || !this.composerText.trim()) {
      return
    }

    await this.rootStore.messageStore.sendMessage({
      conversationId: this.project.conversationId,
      text: this.composerText
    })

    runInAction(() => {
      this.composerText = ''
    })
  }

  async loadMessages () {
    if (!this.project.provider) {
      runInAction(() => {
        this.loadingMessages = false
      })

      return
    }

    runInAction(() => {
      this.loadingMessages = true
    })

    const messageStore = this.rootStore.messageStore
    try {
      await messageStore.fetchMessagesForConversation(this.project.conversationId)
    } finally {
      runInAction(() => {
        this.loadingMessages = false
      })
    }
  }

  @task loadEvents () {
    return this.rootStore.eventStore.fetchJobEvents(this.project.id)
  }

  async loadDocuments () {
    this.rootStore.documentStore.fetchDocuments()
  }
}



// WEBPACK FOOTER //
// ./src/client/store/ui/ProjectDetailsStore.js